import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Square, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getScrollableAncestors } from './institution-select-portal';
import { InstitutionStyledSearchInput } from './InstitutionStyledSearchInput';

export type InstitutionMultiSelectOption = {
  value: string;
  label: string;
  disabled?: boolean;
};

export type InstitutionMultiSelectProps = {
  id?: string;
  label?: ReactNode;
  hint?: ReactNode;
  options: InstitutionMultiSelectOption[];
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
  className?: string;
  panelZIndexClass?: string;
  /** Texte du bouton quand aucune option n’est sélectionnée. */
  emptySummaryLabel: string;
  /** Résumé quand plusieurs IDs sont sélectionnés (le cas « 1 » affiche le libellé de l’option). */
  formatMultipleSummary?: (count: number) => string;
  placeholderSearch?: string;
  noResultsLabel?: string;
  /** Accessibilité : libellé du bouton retirer sur une pastille. */
  formatRemoveChipAria?: (itemLabel: string) => string;
};

type MenuCoords = { top: number; left: number; width: number };

function IconCell({ selected }: { selected: boolean }) {
  return (
    <span
      className={cn(
        'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border',
        selected
          ? 'border-primary-200 bg-primary-50 text-primary-600 dark:border-primary-800/60 dark:bg-primary-950/50 dark:text-primary-300'
          : 'border-transparent bg-white text-[#5B6472] dark:bg-[#122644]/80 dark:text-[#B6CAEC]',
      )}
    >
      <Square className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
    </span>
  );
}

/**
 * Multi-sélection (filtres BI, etc.) : pastilles + champ de recherche + liste cohérente avec InstitutionStyledSelect.
 * Panneau en portail pour éviter le rognage dans les conteneurs à défilement.
 */
export function InstitutionMultiSelect({
  id: idProp,
  label,
  hint,
  options,
  value,
  onChange,
  disabled,
  className,
  panelZIndexClass = 'z-[120]',
  emptySummaryLabel,
  formatMultipleSummary,
  placeholderSearch = 'Rechercher…',
  noResultsLabel = 'Aucun résultat',
  formatRemoveChipAria = (itemLabel) => `Retirer ${itemLabel}`,
}: InstitutionMultiSelectProps) {
  const genId = useId();
  const baseId = idProp ?? `inst-multi-${genId.replace(/:/g, '')}`;
  const labelId = `${baseId}-label`;
  const listboxId = `${baseId}-listbox`;
  const searchId = `${baseId}-search`;
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuPanelRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [menuCoords, setMenuCoords] = useState<MenuCoords | null>(null);

  const byValue = useMemo(() => {
    const m = new Map<string, string>();
    for (const o of options) m.set(o.value, o.label);
    return m;
  }, [options]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  const summary =
    value.length === 0
      ? emptySummaryLabel
      : value.length === 1
        ? (byValue.get(value[0]!) ?? value[0])
        : formatMultipleSummary?.(value.length) ?? `${value.length} sélections`;

  const syncMenuPosition = useCallback(() => {
    const btn = triggerRef.current;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    setMenuCoords({ top: r.bottom + 4, left: r.left, width: r.width });
  }, []);

  useLayoutEffect(() => {
    if (!open || disabled) {
      setMenuCoords(null);
      return;
    }
    syncMenuPosition();
    const btn = triggerRef.current;
    if (!btn) return;
    const scrollParents = getScrollableAncestors(btn);
    const opts = { capture: true } as const;
    scrollParents.forEach((p) => p.addEventListener('scroll', syncMenuPosition, opts));
    window.addEventListener('resize', syncMenuPosition);
    return () => {
      scrollParents.forEach((p) => p.removeEventListener('scroll', syncMenuPosition, opts));
      window.removeEventListener('resize', syncMenuPosition);
    };
  }, [open, disabled, syncMenuPosition]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (containerRef.current?.contains(t)) return;
      if (menuPanelRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const toggle = useCallback(
    (v: string, optDisabled?: boolean) => {
      if (optDisabled) return;
      if (value.includes(v)) onChange(value.filter((id) => id !== v));
      else onChange([...value, v]);
    },
    [onChange, value],
  );

  const remove = useCallback(
    (v: string) => {
      onChange(value.filter((id) => id !== v));
    },
    [onChange, value],
  );

  if (options.length === 0) {
    return null;
  }

  const menuPanel =
    open && !disabled && menuCoords ? (
      <div
        ref={menuPanelRef}
        style={{
          position: 'fixed',
          top: menuCoords.top,
          left: menuCoords.left,
          width: menuCoords.width,
          margin: 0,
        }}
        className={cn(
          'flex max-h-[min(320px,50vh)] flex-col gap-1.5 overflow-hidden rounded-xl border border-[#E8EDF5]/90 bg-[#F8FAFC]/95 p-1.5 shadow-lg ring-1 ring-black/5 dark:border-[#B6CAEC] dark:bg-[#0C1C38]/95 dark:ring-white/10',
          panelZIndexClass,
        )}
      >
        <div className="shrink-0 px-0.5 pt-0.5">
          <InstitutionStyledSearchInput
            dense
            id={searchId}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={placeholderSearch}
            autoComplete="off"
            onKeyDown={(e) => e.stopPropagation()}
            aria-controls={listboxId}
          />
        </div>
        <ul
          id={listboxId}
          role="listbox"
          aria-multiselectable="true"
          aria-labelledby={label != null && label !== false ? labelId : undefined}
          className="m-0 flex min-h-0 flex-1 list-none flex-col gap-1 overflow-y-auto p-0"
        >
          {filtered.length === 0 ? (
            <li className="list-none px-2 py-3 text-center text-xs text-[#5B6472] dark:text-[#B6CAEC]">{noResultsLabel}</li>
          ) : (
            filtered.map((o) => {
              const isSel = value.includes(o.value);
              return (
                <li key={o.value} role="presentation" className="list-none">
                  <button
                    type="button"
                    role="option"
                    aria-selected={isSel}
                    disabled={o.disabled}
                    onClick={() => toggle(o.value, o.disabled)}
                    className={cn(
                      'flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-start text-sm transition-all',
                      isSel
                        ? 'bg-white text-primary-900 shadow-sm ring-2 ring-primary-500/35 dark:bg-[#122644] dark:text-primary-50 dark:ring-primary-400/40'
                        : 'text-[#121826] hover:bg-white/90 hover:shadow-sm dark:text-[#DCE7F7] dark:hover:bg-[#122644]/90',
                      o.disabled && 'cursor-not-allowed opacity-50',
                    )}
                  >
                    <IconCell selected={isSel} />
                    <span className="min-w-0 flex-1 font-medium leading-snug">{o.label}</span>
                    <span
                      className={cn(
                        'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 transition-colors',
                        isSel
                          ? 'border-primary-600 bg-primary-900 dark:border-primary-400 dark:bg-primary-400'
                          : 'border-[#E8EDF5] bg-transparent dark:border-[#5C82C5]',
                      )}
                      aria-hidden
                    >
                      {isSel ? <span className="h-1.5 w-1.5 rounded-full bg-white dark:bg-[#0C1C38]" /> : null}
                    </span>
                  </button>
                </li>
              );
            })
          )}
        </ul>
      </div>
    ) : null;

  return (
    <div className={cn('w-full', className)}>
      {label != null && label !== false ? (
        <span id={labelId} className="mb-1.5 block text-xs font-medium text-[#121826] dark:text-[#DCE7F7]">
          {label}
        </span>
      ) : null}

      {value.length > 0 ? (
        <div className="mb-2 flex flex-wrap gap-1.5" aria-label={typeof label === 'string' ? `${label} — sélection` : undefined}>
          {value.map((id) => (
            <span
              key={id}
              className="inline-flex max-w-full items-center gap-1 rounded-full border border-primary-200/80 bg-primary-50/90 px-2.5 py-0.5 text-xs font-medium text-primary-900 dark:border-primary-800/50 dark:bg-primary-950/40 dark:text-primary-100"
            >
              <span className="min-w-0 truncate">{byValue.get(id) ?? id}</span>
              <button
                type="button"
                disabled={disabled}
                onClick={() => !disabled && remove(id)}
                className="shrink-0 rounded-full p-0.5 text-primary-700 hover:bg-primary-100 dark:text-primary-200 dark:hover:bg-primary-900/50"
                aria-label={formatRemoveChipAria(byValue.get(id) ?? id)}
              >
                <X className="h-3 w-3" aria-hidden />
              </button>
            </span>
          ))}
        </div>
      ) : null}

      <div ref={containerRef} className="relative">
        <button
          ref={triggerRef}
          type="button"
          id={baseId}
          disabled={disabled}
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-controls={listboxId}
          aria-multiselectable
          aria-labelledby={label != null && label !== false ? labelId : undefined}
          aria-label={label == null || label === false ? summary : undefined}
          onClick={() => !disabled && setOpen((o) => !o)}
          className={cn(
            'flex w-full items-center gap-3 rounded-lg border px-2.5 py-2 text-start text-sm transition-all',
            'border-[#E8EDF5]/90 bg-[#F8FAFC]/80 dark:border-[#B6CAEC] dark:bg-[#0C1C38]/50',
            'bg-white text-primary-900 shadow-sm ring-2 ring-primary-500/35 dark:bg-[#122644] dark:text-primary-50 dark:ring-primary-400/40',
            disabled && 'cursor-not-allowed opacity-50',
          )}
        >
          <IconCell selected={value.length > 0} />
          <span className="min-w-0 flex-1 font-medium leading-snug">{summary}</span>
          <ChevronDown
            className={cn('h-4 w-4 shrink-0 text-[#5B6472] transition-transform dark:text-[#B6CAEC]', open && 'rotate-180')}
            aria-hidden
          />
        </button>
      </div>
      {typeof document !== 'undefined' && menuPanel ? createPortal(menuPanel, document.body) : null}
      {hint ? <p className="mt-1 text-[11px] text-[#5B6472] dark:text-[#B6CAEC]">{hint}</p> : null}
    </div>
  );
}
